import { router } from '@vue-storefront/core/app'

export function afterRegistration ({ Vue, config, store, isServer }) {
  if (config.googleTagManager.id && !isServer) {
    const CURRENCY = config.i18n.currencyCode
    // Per GA4 docs: items[] is a representative sample, not a payload dump - our listings and
    // multi-item orders routinely exceed this, so it's capped for payload size. value must NOT
    // be derived from the capped items[] though - it has to reflect the true total regardless of
    // how many items got truncated, since it's what drives revenue reporting.
    const MAX_ITEMS = 20

    // Anywhere ("call a courier") and Insurance checkout never touch the shop cart - see the
    // purchase-event handler below for why that matters for GA4/Facebook Pixel tracking.
    const AD_HOC_PARTNER_CODES = ['anywhere', 'insurance']

    const getEffectivePrice = (item) => {
      const listPrice = parseFloat(item.price) || 0
      const specialPrice = parseFloat(item.special_price)
      let price = (specialPrice > 0 && specialPrice < listPrice) ? specialPrice : listPrice
      if (item.selected_options && item.selected_options.length) {
        price += item.selected_options.reduce((sum, option) => sum + (parseFloat(option.price) || 0) * (option.qty || 1), 0)
      }
      return price
    }

    const getItem = (item, affiliation, index = 0, customList = null) => {
      let category, categoryId, quantity
      if (item.category && item.category.length) {
        const lastCat = item.category.slice(-1)[0]
        category = lastCat.name
        categoryId = lastCat.category_id
      }
      if (item.qty) {
        quantity = parseFloat(item.qty)
      }
      if (!quantity) {
        quantity = item.min_sale_qty ? parseFloat(item.min_sale_qty) : 1.0
      }
      return {
        item_id: item.sku,
        item_name: item.name,
        price: getEffectivePrice(item),
        quantity: quantity,
        affiliation,
        item_brand: affiliation,
        item_category: category,
        item_list_id: customList ? customList.id : categoryId,
        item_list_name: customList ? customList.name : category,
        index
      }
    }

    const recommendationList = (type) => type ? { id: `recommendations_${type}`, name: `Recommendations: ${type}` } : null

    const currentPartnerName = () =>
      store.getters['partners/getCurrentPartner']
        ? store.getters['partners/getCurrentPartner'].title
        : store.state.partners.current_partner_code

    // recommended is set at fetch time on recommendation/related/upsell slider items (see
    // loadRecommendations, ProductRelated.vue, UpsellSlider.vue) and survives on client-side
    // cartItems (full object spread on add), but every checkout/purchase event reads from
    // groupedCart instead, which is wholesale-replaced by the backend's cart-quote response and
    // never carries the flag. Join by sku against cartItems to recover it for those events.
    const getRecommendedListForSku = (sku) => {
      const cartItem = store.getters['cart/getCartItems'].find(it => it.sku === sku)
      return cartItem && cartItem.recommended ? recommendationList(cartItem.recommended) : null
    }

    // Walks the full product list to compute the true value (every item counts), while items[]
    // only keeps the first MAX_ITEMS built items - value and items are intentionally decoupled.
    const summarize = (products, itemFor) => {
      let value = 0
      const items = []
      products.forEach((product, index) => {
        const item = itemFor(product, index)
        value += item.price * item.quantity
        if (index < MAX_ITEMS) items.push(item)
      })
      return { items, value }
    }

    let lastViewItemSku = null
    const trackViewItem = (product, affiliation) => {
      if (!product || lastViewItemSku === product.sku) return
      lastViewItemSku = product.sku
      Vue.$gtag.event('view_item', {
        currency: CURRENCY,
        value: getEffectivePrice(product),
        items: [getItem(product, affiliation)]
      })
    }

    const trackViewItemList = (products, affiliation, customList = null) => {
      if (!products || !products.length) return
      const { items, value } = summarize(products, (product, index) => getItem(product, affiliation, index, customList))
      Vue.$gtag.event('view_item_list', {
        currency: CURRENCY,
        value,
        items
      })
    }

    // Shared by begin_checkout / add_shipping_info / add_payment_info / purchase / view_cart -
    // every event that walks one partner group of the multi-partner grouped cart.
    const summarizeGroup = (group, customList = null) =>
      summarize(group.items, (product, index) => getItem(product, group.partner.location_code, index, customList || getRecommendedListForSku(product.sku)))

    // purchase fires one event per group (each group's items[] is already correctly 0-indexed on
    // its own), but begin_checkout/add_shipping_info/add_payment_info/view_cart concatenate every
    // group into a single event - re-flatten the index so it's unique across the combined list
    // actually sent, instead of restarting at 0 for each group's own capped slice.
    const reindexed = (items) => items.map((item, index) => ({ ...item, index }))

    const summarizeAllGroups = (customList = null) => {
      let value = 0
      let items = []
      store.getters['partners/getGroupedByPartnerProducts'].forEach(group => {
        const summary = summarizeGroup(group, customList)
        value += summary.value
        items = items.concat(summary.items)
      })
      return { items: reindexed(items), value }
    }

    Vue.prototype.$bus.$on('recommendations-viewed', ({ items, listId, listName }) => {
      try {
        trackViewItemList(items, currentPartnerName(), listId ? { id: listId, name: listName } : null)
      } catch (e) {
        console.error('GTM tracking error:', e)
      }
    })

    store.subscribe(({ type, payload }, state) => {
      try {
        /** Product list view **/
        if (type === 'product/product/UPD_PRODUCTS') {
          trackViewItemList(payload.products.items, currentPartnerName())
        }

        /** Product detail view **/
        if (type === 'product/product/SET_PRODUCT_ORIGINAL') {
          const affiliation = currentPartnerName()

          Vue.$gtag.event('select_item', {
            items: [getItem(payload, affiliation, 0)]
          })
        }

        /** Current product view **/
        if (type === 'product/product/SET_PRODUCT_CURRENT') {
          trackViewItem(payload, currentPartnerName())
        }

        /** Product Recommendations view (PDP fbt/related/discontinued/disabled_partner slider) **/
        if (type === 'product/product/SET_RECOMMENDATIONS') {
          trackViewItemList(payload.items, currentPartnerName(), recommendationList(payload.type))
        }

        /** "More from the section" related-products slider (quick-view modal) **/
        if (type === 'product/product/UPD_RELATED') {
          trackViewItemList(payload.items, currentPartnerName(), { id: `related_${payload.key}`, name: `Related: ${payload.key}` })
        }

        /** Add to cart **/
        if (type === 'cart/cart/ADD') {
          const item = getItem(payload.product, currentPartnerName(), 0, recommendationList(payload.product.recommended))
          Vue.$gtag.event('add_to_cart', {
            currency: CURRENCY,
            value: item.price * item.quantity,
            items: [item]
          })
        }

        /** Remove from cart **/
        if (type === 'cart/cart/DEL') {
          const item = getItem(payload.product, currentPartnerName(), 0, recommendationList(payload.product.recommended))
          Vue.$gtag.event('remove_from_cart', {
            currency: CURRENCY,
            value: item.price * item.quantity,
            items: [item]
          })
        }

        /** Begin checkout **/
        if (type === 'route/ROUTE_CHANGED') {
          // Process SSR cases of routing
          if (payload.from && !payload.from.name) {
            // Track PDP loading
            if (payload.to.params && payload.to.params.store && payload.to.params.locationCode && (payload.to.params.parentSku || payload.to.params.childSku)) {
              trackViewItem(store.getters['product/productCurrent'], currentPartnerName())
            } else if (payload.to.params && payload.to.params.store && payload.to.params.locationCode) { // Track Partner View Page Loading
              trackViewItemList(store.getters['product/list'], currentPartnerName())
            }
          }
          if (payload.to.name === 'checkout' && payload.to.hash === '') {
            const { items, value } = summarizeAllGroups()
            Vue.$gtag.event('begin_checkout', {
              currency: CURRENCY,
              value,
              items
            })
          }
        }

        /** Add shipping info **/
        if (type === 'checkout/checkout/SAVE_DELIVERYTIME' && router.currentRoute.name === 'checkout') {
          const { items, value } = summarizeAllGroups()
          Vue.$gtag.event('add_shipping_info', {
            currency: CURRENCY,
            value,
            items
          })
        }

        /** Add payment info **/
        if (type === 'checkout/checkout/SAVE_PAYMENT_DETAILS' && router.currentRoute.name === 'checkout') {
          const { items, value } = summarizeAllGroups()
          Vue.$gtag.event('add_payment_info', {
            currency: CURRENCY,
            value,
            items
          })
        }

        /** Purchase **/
        if (type === 'order/order/LAST_ORDER_CONFIRMATION') {
          const orders = payload.confirmation.orders || []

          // Anywhere/Insurance checkout (K2-2595) never touches the shop cart - there is no
          // matching partner group in getGroupedByPartnerProducts, so the purchase event below
          // would otherwise never fire for these orders (no error, just silently skipped).
          // Build it from the order response instead, which now always carries a real item.
          if (AD_HOC_PARTNER_CODES.includes(payload.order.partner_code)) {
            const order = orders.find(o => o.partner_code === payload.order.partner_code) || orders[0]
            if (order) {
              const requestComment = (payload.order.comments || []).find(comment => comment.partner === payload.order.partner_code)
              const affiliation = currentPartnerName()

              Vue.$gtag.event('purchase', {
                transaction_id: order.connection_order_id,
                value: parseFloat(order.grand_total) || 0,
                shipping: parseFloat(order.shipping_amount) || 0,
                currency: CURRENCY,
                items: [{
                  item_id: payload.order.partner_code === 'anywhere' ? 'ANY' : payload.order.partner_code.toUpperCase(),
                  // Insurance checkout always sends an empty comment string (see Insurance.vue),
                  // so check the text itself, not just whether a matching comment object exists -
                  // otherwise item_name silently ends up blank instead of falling back.
                  item_name: (requestComment && requestComment.comment) ? requestComment.comment : payload.order.partner_code,
                  price: 0,
                  quantity: 1,
                  affiliation,
                  item_brand: affiliation
                }]
              })
            }
            return
          }

          store.getters['partners/getGroupedByPartnerProducts'].forEach(group => {
            const { items, value } = summarizeGroup(group)
            const delivery = group.delivery || 0
            // Multi-partner checkout creates one backend order per partner, each with its own
            // connection_order_id - use that instead of magentoOrderId (which only ever holds the
            // first order) so transaction_id is naturally unique per group without gluing a
            // partner-code suffix onto a shared id (GA4 expects one purchase per unique
            // transaction_id, or every group but one gets silently dropped/deduped).
            const groupOrder = orders.find(order => order.partner_code === group.partner.code)

            Vue.$gtag.event('purchase', {
              transaction_id: groupOrder ? groupOrder.connection_order_id : payload.confirmation.magentoOrderId,
              value: value + delivery,
              shipping: delivery,
              currency: CURRENCY,
              items
            })
          })
        }

        if (type === 'ui/setMicrocart' && payload === true) {
          // The cart-icon click (MicrocartIcon.js openMicrocart) fires dispatch('cart/load') and
          // dispatch('ui/toggleMicrocart') back-to-back without awaiting the former, so this
          // mutation always commits before the reload resolves - reading groupedCart synchronously
          // here would always see stale/empty data (groupedCart is only ever written by the async
          // cart/load -> CART_LOAD_CART, never by the synchronous add-to-cart mutation). Await our
          // own load first so the event reflects what the user is about to actually see.
          store.dispatch('cart/load').then(() => {
            const { items, value } = summarizeAllGroups({ id: 'cart_view', name: 'Cart view' })
            Vue.$gtag.event('view_cart', {
              currency: CURRENCY,
              value,
              items
            })
          }).catch(e => console.error('GTM tracking error:', e))
        }
      } catch (e) {
        console.error('GTM tracking error:', e)
      }
    })
  }
}
